import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCalls } from './hooks/useCalls';
import { CallMetricsCards, ActiveCallsTable, CallHistoryTable } from './components';
import { PageLoader, FullWidthContainer, Input, Select, Modal, Button } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { useBlockedNumbers } from '../BlockedNumbers/hooks/useBlockedNumbers';
import { useToast } from '../../contexts/useToast';
import { Call } from './types';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  'initiated',
  'ringing',
  'answered',
  'in_progress',
  'completed',
  'failed',
  'cancelled',
  'busy',
  'no_answer',
  'transferred',
  'escalated',
] as const;

const Calls: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { blockFromCall } = useBlockedNumbers();

  const getStatusTranslation = useCallback(
    (status: string) => {
      switch (status) {
        case 'initiated':
          return t('calls.initiated');
        case 'ringing':
          return t('calls.ringing');
        case 'answered':
          return t('calls.answered');
        case 'in_progress':
          return t('calls.inProgress');
        case 'completed':
          return t('calls.completed');
        case 'failed':
          return t('calls.failed');
        case 'cancelled':
          return t('calls.cancelled');
        case 'busy':
          return t('calls.busy');
        case 'no_answer':
          return t('calls.noAnswer');
        case 'transferred':
          return t('calls.transferred');
        case 'escalated':
          return t('calls.escalated');
        default:
          return status;
      }
    },
    [t]
  );

  const statusSelectOptions = useMemo(
    () => [
      { value: '', label: t('calls.allStatus') },
      ...STATUS_OPTIONS.map(status => ({
        value: status,
        label: getStatusTranslation(status),
      })),
    ],
    [t, getStatusTranslation]
  );
  const {
    metrics,
    activeCalls,
    callHistory,
    totalCount,
    selectedCall,
    loading,
    error,
    hasPermissionError,
    fetchCalls,
    fetchCallsForTab,
    viewActiveCall,
    viewCallHistory,
    startPolling,
    stopPolling,
  } = useCalls();

  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [isCallDetailModalOpen, setIsCallDetailModalOpen] = useState(false);
  // Deep link to one call, e.g. from an unanswered question of the knowledge base
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCallId = searchParams.get('call');

  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const [activeCallsSearch, setActiveCallsSearch] = useState('');
  const [activeCallsStatus, setActiveCallsStatus] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const [activeCallsPage, setActiveCallsPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    fetchCalls(true, activeCallsPage, PAGE_SIZE);
  }, [fetchCalls, activeCallsPage]);

  useEffect(() => {
    if (activeTab === 'active') {
      setActiveCallsPage(1);
    }
  }, [activeCallsSearch, activeCallsStatus, activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      setHistoryPage(1);
    }
  }, [historySearch, historyStatus, historyDateFrom, historyDateTo, activeTab]);

  useEffect(() => {
    if (activeTab === 'active') {
      startPolling();
    } else {
      stopPolling();
    }
  }, [activeTab, startPolling, stopPolling]);

  const handleTabChange = (tab: 'active' | 'history') => {
    setActiveTab(tab);
    if (tab === 'active') {
      setActiveCallsPage(1);
      fetchCallsForTab(true, 1, PAGE_SIZE);
      startPolling();
    } else {
      setHistoryPage(1);
      fetchCallsForTab(false, 1, PAGE_SIZE);
      stopPolling();
    }
  };

  const handleActiveCallsPageChange = (page: number) => {
    setActiveCallsPage(page);
    fetchCallsForTab(true, page, PAGE_SIZE);
  };

  const handleHistoryPageChange = (page: number) => {
    setHistoryPage(page);
    fetchCallsForTab(false, page, PAGE_SIZE);
  };

  useEffect(() => {
    if (!linkedCallId) return;
    setActiveTab('history');
    viewCallHistory(linkedCallId).then(() => setIsCallDetailModalOpen(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCallId]);

  const closeCallDetail = () => {
    setIsCallDetailModalOpen(false);
    if (linkedCallId) setSearchParams({}, { replace: true });
  };

  const handleViewCall = async (callId: string) => {
    if (activeTab === 'active') {
      await viewActiveCall(callId);
    } else {
      await viewCallHistory(callId);
    }
    setIsCallDetailModalOpen(true);
  };

  const handleBlockNumber = async (callId: string, callerNumber: string) => {
    const result = await blockFromCall({ call_id: callId });
    if (result.success) {
      showToast({
        type: 'success',
        title: t('blockedNumbers.numberBlocked'),
        message: t('blockedNumbers.numberBlockedMessage', { number: callerNumber }),
      });
    } else {
      showToast({
        type: 'error',
        title: t('blockedNumbers.addError'),
        message: result.error || t('blockedNumbers.addError'),
      });
    }
  };

  const filterCalls = (
    calls: Call[],
    search: string,
    status: string,
    dateFrom?: string,
    dateTo?: string
  ) => {
    return calls.filter(call => {
      const matchesSearch =
        call.id.toLowerCase().includes(search.toLowerCase()) ||
        call.agent?.name?.toLowerCase().includes(search.toLowerCase()) ||
        call.caller_number.includes(search);
      const matchesStatus = status === '' || call.status === status;

      if (dateFrom || dateTo) {
        const callDate = new Date(call.started_at);
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;
        const matchesDate = (!fromDate || callDate >= fromDate) && (!toDate || callDate <= toDate);
        return matchesSearch && matchesStatus && matchesDate;
      }

      return matchesSearch && matchesStatus;
    });
  };

  const filteredActiveCalls = useMemo(
    () => filterCalls(activeCalls, activeCallsSearch, activeCallsStatus),
    [activeCalls, activeCallsSearch, activeCallsStatus]
  );

  const filteredCallHistory = useMemo(
    () => filterCalls(callHistory, historySearch, historyStatus, historyDateFrom, historyDateTo),
    [callHistory, historySearch, historyStatus, historyDateFrom, historyDateTo]
  );

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error && hasPermissionError) {
    return (
      <FullWidthContainer>
        <div className="text-center py-8">
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
            <span className="material-icons text-2xl text-red-600 dark:text-red-400">lock</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('calls.accessRestricted')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {t('calls.accessRestrictedMessage')}
          </p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => window.location.reload()} className="mr-3">
              <span className="material-icons text-sm mr-1">refresh</span>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('calls.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('calls.subtitle')}</p>
          </div>
        </div>

        {error && !hasPermissionError && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="material-icons text-yellow-600 dark:text-yellow-400 text-lg">
                warning
              </span>
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="font-semibold">{t('calls.unableToLoadCallData')}</p>
                <p className="mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <CallMetricsCards metrics={metrics} />

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('active')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'active'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <span className="material-icons text-sm mr-2">phone</span>
              {t('calls.activeCalls')}
            </button>
            <button
              onClick={() => handleTabChange('history')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'history'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <span className="material-icons text-sm mr-2">history</span>
              {t('calls.callHistory')}
            </button>
          </nav>
        </div>

        {activeTab === 'active' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('calls.activeCalls')}
                </h2>
              </div>
              <div className="flex gap-4">
                <Input
                  type="text"
                  placeholder={t('calls.searchCalls')}
                  value={activeCallsSearch}
                  onChange={e => setActiveCallsSearch(e.target.value)}
                  className="w-80 h-10"
                />
                <Select
                  value={activeCallsStatus}
                  onChange={value => setActiveCallsStatus(Array.isArray(value) ? value[0] : value)}
                  options={statusSelectOptions}
                  className="w-40 h-10"
                />
              </div>
            </div>
            <ActiveCallsTable
              calls={filteredActiveCalls}
              onViewCall={handleViewCall}
              page={activeCallsPage}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={handleActiveCallsPageChange}
            />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('calls.callHistory')}
                </h2>
              </div>
              <div className="flex gap-4">
                <Input
                  type="text"
                  placeholder={t('calls.searchCalls')}
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  className="w-80 h-10"
                />
                <Input
                  type="date"
                  value={historyDateFrom}
                  onChange={e => setHistoryDateFrom(e.target.value)}
                  className="w-40 h-10"
                  title={t('calls.fromDate')}
                />
                <Input
                  type="date"
                  value={historyDateTo}
                  onChange={e => setHistoryDateTo(e.target.value)}
                  className="w-40 h-10"
                  title={t('calls.toDate')}
                />
                <Select
                  value={historyStatus}
                  onChange={value => setHistoryStatus(Array.isArray(value) ? value[0] : value)}
                  options={statusSelectOptions}
                  className="w-40 h-10"
                />
              </div>
            </div>
            <CallHistoryTable
              calls={filteredCallHistory}
              onViewCall={handleViewCall}
              onBlockNumber={handleBlockNumber}
              page={historyPage}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={handleHistoryPageChange}
            />
          </div>
        )}

        <Modal
          isOpen={isCallDetailModalOpen}
          onClose={closeCallDetail}
          title={t('calls.callDetails')}
          size="xl"
        >
          {selectedCall && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.callId')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">{selectedCall.id}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.status')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {getStatusTranslation(selectedCall.status)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.direction')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCall.direction_display}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.duration')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCall.duration} {t('calls.seconds')}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.callerNumber')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCall.caller_number}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.calledNumber')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCall.called_number}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.startedAt')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {new Date(selectedCall.started_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('calls.endedAt')}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCall.ended_at
                      ? new Date(selectedCall.ended_at).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  {t('calls.agentInformation')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('calls.agentName')}
                    </h4>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCall.agent?.name || t('calls.noAgent')}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('calls.voice')}
                    </h4>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCall.agent?.voice?.name || t('calls.noVoice')}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('calls.language')}
                    </h4>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCall.agent?.language?.title || t('calls.noLanguage')}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('calls.knowledgeBase')}
                    </h4>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCall.agent?.knowledge_base?.name || t('calls.noKnowledgeBase')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  {t('calls.callRoutes')}
                </h3>
                {(() => {
                  const callRoutes =
                    selectedCall.call_routes && selectedCall.call_routes.length > 0
                      ? selectedCall.call_routes
                      : selectedCall.agent?.call_routes || [];
                  if (callRoutes.length > 0) {
                    return (
                      <div className="space-y-4">
                        {callRoutes.map(route => (
                          <div
                            key={route.id}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900/30"
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div>
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                                  {route.name}
                                </h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {route.phone_number}
                                </p>
                              </div>
                              {route.status && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                                  {route.status}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  {t('calls.businessHours')}
                                </h5>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {route.business_hours || t('calls.notAvailable')}
                                </p>
                              </div>
                              {route.created && (
                                <div>
                                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    {t('calls.createdAt')}
                                  </h5>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {new Date(route.created).toLocaleString()}
                                  </p>
                                </div>
                              )}
                              {route.modified && (
                                <div>
                                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    {t('calls.updatedAt')}
                                  </h5>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {new Date(route.modified).toLocaleString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  if (selectedCall.call_route_name || selectedCall.organization_name) {
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCall.call_route_name && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('calls.routeName')}
                            </h4>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {selectedCall.call_route_name}
                            </p>
                          </div>
                        )}
                        {selectedCall.organization_name && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('calls.organization')}
                            </h4>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {selectedCall.organization_name}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('calls.noCallRoutes')}
                    </p>
                  );
                })()}
              </div>

              {selectedCall.conversation && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    {t('calls.conversation')}
                  </h3>

                  {selectedCall.conversation.summary && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        {t('calls.summary')}
                      </h4>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap leading-relaxed">
                          {selectedCall.conversation.summary.split('\n').map((line, index) => {
                            const isBold =
                              line.trim().startsWith('**') && line.trim().endsWith('**');
                            const isListItem = /^\d+\.\s+\*\*/.test(line.trim());

                            if (isBold) {
                              const text = line.replace(/\*\*/g, '').trim();
                              return (
                                <p
                                  key={index}
                                  className="font-semibold text-base mb-2 mt-3 first:mt-0"
                                >
                                  {text}
                                </p>
                              );
                            }

                            if (isListItem) {
                              const text = line.replace(/\*\*/g, '').trim();
                              return (
                                <p key={index} className="ml-4 mb-1">
                                  {text}
                                </p>
                              );
                            }

                            if (line.trim() === '') {
                              return <br key={index} />;
                            }

                            return (
                              <p key={index} className="mb-2">
                                {line.replace(/\*\*/g, '').trim()}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedCall.conversation.metadata?.summary_routing && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 border border-blue-200 dark:border-blue-800">
                        <span className="material-icons text-blue-500 text-sm">alt_route</span>
                        <span>
                          {t('calls.summaryRoutedTo')}:{' '}
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {selectedCall.conversation.metadata.summary_routing.routed_to.join(
                              ', '
                            )}
                          </span>
                        </span>
                        {selectedCall.conversation.metadata.summary_routing.matched_rule && (
                          <span className="text-gray-400 dark:text-gray-500">
                            — {selectedCall.conversation.metadata.summary_routing.matched_rule}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {(selectedCall.conversation.metadata?.blocked_transfers?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        {t('calls.blockedTransfers')}
                      </h4>
                      <div className="space-y-2">
                        {selectedCall.conversation.metadata!.blocked_transfers!.map((bt, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 border border-amber-200 dark:border-amber-800"
                          >
                            <span className="material-icons text-amber-500 text-sm">block</span>
                            <span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {bt.route}
                              </span>{' '}
                              {bt.reason === 'outside_business_hours'
                                ? t('calls.blockedReasonOutsideBusinessHours')
                                : bt.reason === 'missing_variables'
                                  ? t('calls.blockedReasonMissingVariables', {
                                      variables: (bt.missing || []).join(', '),
                                    })
                                  : bt.reason}
                            </span>
                            <span className="ml-auto whitespace-nowrap text-gray-400 dark:text-gray-500">
                              {new Date(bt.at).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedCall.conversation.metadata?.messages_left?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        {t('calls.messagesLeft')}
                      </h4>
                      <div className="space-y-2">
                        {selectedCall.conversation.metadata!.messages_left!.map((ml, idx) => (
                          <div
                            key={idx}
                            className="text-xs text-gray-600 dark:text-gray-300 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2 border border-emerald-200 dark:border-emerald-800"
                          >
                            <div className="flex items-center gap-2">
                              <span className="material-icons text-emerald-500 text-sm">
                                voicemail
                              </span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {t('calls.messageFor', { route: ml.route })}
                              </span>
                              {ml.callback_number && (
                                <span>
                                  · {t('calls.callbackNumber')}: {ml.callback_number}
                                </span>
                              )}
                              <span className="ml-auto whitespace-nowrap text-gray-400 dark:text-gray-500">
                                {new Date(ml.at).toLocaleString()}
                              </span>
                            </div>
                            <p className="mt-1 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {ml.message}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCall.conversation.messages &&
                    selectedCall.conversation.messages.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                          {t('calls.messages')} ({selectedCall.conversation.messages.length})
                        </h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="max-h-96 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                            {[...selectedCall.conversation.messages]
                              .sort((a, b) => {
                                const timeA = new Date(a.timestamp || a.created).getTime();
                                const timeB = new Date(b.timestamp || b.created).getTime();
                                return timeA - timeB;
                              })
                              .map(message => (
                                <div
                                  key={message.id}
                                  className={`flex gap-3 ${
                                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                                  }`}
                                >
                                  <div
                                    className={`flex-1 rounded-lg p-3 ${
                                      message.role === 'user'
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                                        : message.role === 'assistant'
                                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600'
                                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <span
                                        className={`text-xs font-semibold uppercase ${
                                          message.role === 'user'
                                            ? 'text-primary-700 dark:text-primary-300'
                                            : message.role === 'assistant'
                                              ? 'text-gray-600 dark:text-gray-400'
                                              : 'text-gray-500 dark:text-gray-500'
                                        }`}
                                      >
                                        {message.role === 'user'
                                          ? t('calls.user')
                                          : message.role === 'assistant'
                                            ? t('calls.assistant')
                                            : t('calls.system')}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {new Date(
                                          message.timestamp || message.created
                                        ).toLocaleTimeString()}
                                      </span>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap break-words">
                                      {message.content}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                  {selectedCall.conversation.status && (
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('calls.conversationStatus')}:
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white capitalize">
                        {selectedCall.conversation.status}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={() => setIsCallDetailModalOpen(false)}>
                  {t('calls.close')}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </FullWidthContainer>
  );
};

export { Calls };
